/**
 * Operational timeline models for משימות שינוע 99 — the step-by-step story of
 * one task, from the moment it was opened to the moment a manager closed it.
 *
 * The distinction this layer keeps is the one the board already depends on:
 * the append-only ledger in `./audit` records *who took responsibility* for a
 * decision, while the timeline records *where the task stands right now*. A
 * ledger row is evidence; a timeline event is progress. They are written from
 * the same actions and read on different screens.
 *
 * One row per event, filed in the `timelineEvents` table of database version 8.
 * Nothing here edits an event — the feed is appended to, exactly as the ledger
 * is, so a status that was reported cannot be un-reported.
 *
 * The pure logic that orders, formats and derives progress from these rows
 * lives in `src/core/timeline-engine.ts`; the persistence lives in
 * `src/db/timeline-store.ts`.
 */
import type { ISODateString } from './index';
import type { UserRole } from './user';

// ── The status lifecycle ─────────────────────────────────────────────

/**
 * Every step a task can be reported at, in the order the field walks them.
 * The three terminal steps (`soldier_completed` is not one of them — a soldier
 * finishing is not the task finishing) close the feed.
 */
export const TIMELINE_EVENT_TYPES = [
  'created',
  'teams_assigned',
  'receipt_confirmed',
  'en_route',
  'arrived_origin',
  'loading',
  'in_transit',
  'arrived_destination',
  'unloading',
  'soldier_completed',
  'manager_closed',
  'cancelled',
] as const;

export type TimelineEventType = (typeof TIMELINE_EVENT_TYPES)[number];

/** The default Hebrew headline of a step, when the caller supplies none. */
export const TIMELINE_EVENT_LABELS: Record<TimelineEventType, string> = {
  created: 'המשימה נפתחה',
  teams_assigned: 'שובצו רכבים וצוותים',
  receipt_confirmed: 'אושרה קבלת המשימה',
  en_route: 'יצא לדרך',
  arrived_origin: 'הגיע לנקודת האיסוף',
  loading: 'טעינה',
  in_transit: 'בדרך ליעד',
  arrived_destination: 'הגיע ליעד',
  unloading: 'פריקה',
  soldier_completed: 'החייל סיים ביצוע',
  manager_closed: 'המשימה נסגרה על ידי מנהל',
  cancelled: 'המשימה בוטלה',
};

/** Node palette, in one place, so every screen colours a step alike. */
export const TIMELINE_EVENT_TONES: Record<TimelineEventType, string> = {
  created: 'border-line bg-canvas text-ink/60',
  teams_assigned: 'border-brand-300 bg-brand-50 text-brand-700',
  receipt_confirmed: 'border-brand-300 bg-brand-50 text-brand-700',
  en_route: 'border-amber-300 bg-amber-50 text-amber-800',
  arrived_origin: 'border-amber-300 bg-amber-50 text-amber-800',
  loading: 'border-amber-300 bg-amber-50 text-amber-800',
  in_transit: 'border-amber-300 bg-amber-50 text-amber-800',
  arrived_destination: 'border-amber-300 bg-amber-50 text-amber-800',
  unloading: 'border-amber-300 bg-amber-50 text-amber-800',
  soldier_completed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  manager_closed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  cancelled: 'border-rose-300 bg-rose-50 text-rose-700',
};

/**
 * Icon key per step. The map stays a string here on purpose — this module is
 * imported by the store and by the healthcheck, neither of which may pull React
 * in. `src/modules/tasks/timeline/TaskTimeline.tsx` turns a key into a glyph.
 */
export const TIMELINE_EVENT_ICONS: Record<TimelineEventType, string> = {
  created: 'file-plus',
  teams_assigned: 'users',
  receipt_confirmed: 'check-circle',
  en_route: 'truck',
  arrived_origin: 'map-pin',
  loading: 'package-plus',
  in_transit: 'route',
  arrived_destination: 'flag',
  unloading: 'package-minus',
  soldier_completed: 'check-check',
  manager_closed: 'lock',
  cancelled: 'x-circle',
};

/**
 * How far along the lifecycle a step sits (0 = just opened). Drives the
 * progress bar and the "current step" badge — never eligibility, which stays
 * the permissions engine's answer.
 */
export const TIMELINE_STEP_RANK: Record<TimelineEventType, number> = {
  created: 0,
  teams_assigned: 1,
  receipt_confirmed: 2,
  en_route: 3,
  arrived_origin: 4,
  loading: 5,
  in_transit: 6,
  arrived_destination: 7,
  unloading: 8,
  soldier_completed: 9,
  manager_closed: 10,
  cancelled: 10,
};

/** The steps that end the feed. A soldier's own finish is deliberately not one. */
export const TIMELINE_TERMINAL_EVENTS: readonly TimelineEventType[] = [
  'manager_closed',
  'cancelled',
];

/** The steps a soldier advances from their own phone. */
export const TIMELINE_FIELD_EVENTS: readonly TimelineEventType[] = [
  'en_route',
  'arrived_origin',
  'loading',
  'in_transit',
  'arrived_destination',
  'unloading',
];

// ── The one-touch field bar ──────────────────────────────────────────

/** One button on the driver's quick action bar. */
export interface QuickStatusStep {
  eventType: TimelineEventType;
  /** Read at arm's length, in a moving cab — the glyph carries the meaning. */
  emoji: string;
  label: string;
  /** The line filed with the step, so the feed reads as a sentence. */
  note: string;
}

/**
 * The six taps that move a shipment along, in walking order. Loading and
 * unloading are separate buttons rather than one "פריקה/טעינה" toggle: the
 * feed has to say which of the two actually happened.
 */
export const QUICK_STATUS_STEPS: readonly QuickStatusStep[] = [
  { eventType: 'en_route', emoji: '🚚', label: 'יצאתי לדרך', note: 'יצאתי לדרך' },
  { eventType: 'arrived_origin', emoji: '📍', label: 'הגעתי לאיסוף', note: 'הגעתי לנקודת האיסוף' },
  { eventType: 'loading', emoji: '📦', label: 'טעינה', note: 'התחלתי טעינה' },
  { eventType: 'in_transit', emoji: '🛣️', label: 'בדרך ליעד', note: 'בדרך ליעד' },
  { eventType: 'arrived_destination', emoji: '📍', label: 'הגעתי ליעד', note: 'הגעתי ליעד' },
  { eventType: 'unloading', emoji: '📦', label: 'פריקה', note: 'התחלתי פריקה' },
];

// ── The event ────────────────────────────────────────────────────────

/** Chronological or reverse-chronological — the two ways a feed is read. */
export const TIMELINE_ORDERS = ['asc', 'desc'] as const;

export type TimelineOrder = (typeof TIMELINE_ORDERS)[number];

/**
 * One node on the task's timeline.
 *
 * The actor is denormalised on purpose: `actorName` and `actorRole` are copied
 * onto the row at the moment it is written, so a badge that read
 * "ישראל (חייל)" still reads that way after the account is renamed, suspended
 * or removed from the roster. The feed is a record of what happened, not a
 * live join.
 */
export interface TimelineEvent {
  id: string;
  taskId: string;
  /** The vehicle team the step belongs to, when it belongs to one. */
  teamId?: string;
  eventType: TimelineEventType;
  /** The Hebrew headline of the node, e.g. "הגיע ליעד". */
  statusTitle: string;
  /** The operational line under the headline — the soldier's own words. */
  description?: string;
  actorId: string;
  actorName: string;
  actorRole: UserRole;
  timestamp: ISODateString;
  /** Where it happened, as typed or as carried from the task's destination. */
  locationText?: string;
  metadata?: Record<string, unknown>;
}

/** What a caller hands the engine — the id and the stamp are the engine's job. */
export type TimelineEventDraft = Omit<TimelineEvent, 'id' | 'statusTitle' | 'timestamp'> & {
  id?: string;
  /** Defaults to {@link TIMELINE_EVENT_LABELS} for the step. */
  statusTitle?: string;
  timestamp?: ISODateString;
};
